import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Banner, Card } from '../../components/ui.tsx'
import { asAuthError, post } from '../../lib/api.ts'
import { useSession } from '../../lib/session.tsx'
import {
  AuthCard,
  AuthHeading,
  ConsentBlock,
  NO_CONSENTS,
  PasswordStrength,
  allConsented,
  type Consents,
} from './AuthCard.tsx'
import { GoogleButton } from './GoogleButton.tsx'
import { assessPassword } from '../../../shared/auth.ts'
import type { AuthError, SessionUser } from '../../../shared/auth.ts'

/**
 * Create an account.
 *
 * Two things this form does that a generic sign-up does not, and both are requirements
 * rather than decoration: it takes age and risk consent as three separate agreements, and
 * it tells you the account starts in paper mode with no card.
 */
export function Register() {
  const navigate = useNavigate()
  const { setUser } = useSession()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [consents, setConsents] = useState<Consents>(NO_CONSENTS)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<AuthError | null>(null)

  const verdict = assessPassword(password, email)
  const consented = allConsented(consents)
  const canSubmit = !!email && verdict.acceptable && consented && !busy

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      const { user } = await post<{ user: SessionUser }>('/auth/register', {
        email,
        password,
        ...consents,
      })
      setUser(user)
      navigate('/', { replace: true })
    } catch (error) {
      setErr(asAuthError(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthCard width={440}>
      <AuthHeading title="Create your account" sub="Starts in paper mode. No card required." />

      {err && !err.kind && (
        <Banner tone="flatten" icon="✕" title="Could not create the account">
          {err.error}
        </Banner>
      )}

      <Card pad>
        {/* Above the form, not below it: the shortest path to an account should be the
            first thing offered, and a divider makes clear these are alternatives. */}
        <GoogleButton label="Sign up with Google" />

        <form onSubmit={submit} noValidate>
          <div className="field">
            <label htmlFor="reg-email">Email</label>
            <input
              className="input"
              type="email"
              id="reg-email"
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (err?.kind === 'email_taken') setErr(null)
              }}
              style={err?.kind === 'email_taken' ? { borderColor: 'var(--neg)' } : undefined}
              aria-describedby={err?.kind === 'email_taken' ? 'reg-email-err' : undefined}
            />
            {err?.kind === 'email_taken' && (
              <span className="help warn" id="reg-email-err" role="alert">
                An account already exists for this email. <Link to="/login">Log in instead</Link>, or{' '}
                <Link to="/recover">reset your password</Link>.
              </span>
            )}
          </div>

          <div className="field">
            <label htmlFor="reg-pass">Password</label>
            <input
              className="input"
              type="password"
              id="reg-pass"
              placeholder="At least 10 characters"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={password && !verdict.acceptable ? { borderColor: 'var(--neg)' } : undefined}
            />
            <PasswordStrength password={password} email={email} />
          </div>

          <hr className="hr" />

          <ConsentBlock value={consents} onChange={setConsents} />

          <button type="submit" className="btn primary block lg" style={{ marginTop: 18 }} disabled={!canSubmit}>
            {busy ? 'Creating account…' : 'Create free account'}
          </button>

          {!consented && (
            <p className="u-center u-sm u-faint" style={{ marginTop: 8 }}>
              Confirm all three above to continue.
            </p>
          )}

          <p className="u-center u-sm u-faint" style={{ marginTop: 12 }}>
            Already have an account? <Link to="/login">Log in</Link>
          </p>
        </form>
      </Card>

      <p className="u-center u-faint u-sm" style={{ marginTop: 18 }}>
        KellyStake never holds your funds. The only thing we charge is
        the subscription — and paper mode is free.
      </p>
    </AuthCard>
  )
}
